import { recipeWriteVersion } from '../../../../../services/recipeWriteConflict';
import { productKey } from "../../../../../utils/reports/shared";
import {
    CostPriceAnalysis,
    CostPriceIngredient,
    IngredientAlternative,
    fetchCostPriceAnalysisById,
    updateCostPriceAnalysis,
    toIngredientUpdatePayload,
} from "../../../../../services/costPrice/costPriceService";
import {
    Subrecipe,
    SubrecipeIngredient,
    fetchSubrecipeById,
    updateSubrecipe,
} from "../../../../../services/subrecipes/subrecipesService";
import { useCostPriceStore } from "../../../../../stores/appCostPriceStore";
import { useSubrecipesStore } from "../../../../../stores/appSubrecipesStore";
import { COST_PRICE_TYPE_CHOICE } from "../../../../../utils/costPriceUtils";

/**
 * Cross-dish propagation of ONE alternative product.
 *
 * Alternatives are stored per ingredient row, but a kitchen's "we sometimes buy
 * tuna steak instead of tuna loin" is a fact about the PRODUCT — so the same
 * alternative has to be re-entered on every dish using that product. This module
 * finds those other dishes/subrecipes and writes the alternative into them.
 *
 * Deliberately the only place the propagation rule lives: if alternatives ever
 * move to a product-level model, this is the seam that gets replaced.
 */

export interface AlternativeTarget {
    kind: 'costPrice' | 'subrecipe';
    /** Cost-price analysis id, or subrecipe id. */
    id: string;
    /** Dish / subrecipe name for display. */
    name: string;
    /** That entity's own per-portion quantity of the PRIMARY product. */
    primaryQuantity: number;
    /** Unit the primary quantity is expressed in (display only). */
    primaryUnit: string;
    /** Resolver keys of the alternatives already on that row — drives "already added". */
    existingAlternativeKeys: string[];
}

/** Order-history row test — same shape check the alternatives editor uses. */
function isNormalCostPriceRow(ing: CostPriceIngredient): boolean {
    return !ing.custom && !(ing.subrecipeId && !ing.articleId) && !ing.nestedCostPriceId;
}

/** Subrecipe twin: alternatives are stripped from custom and nested-reference rows. */
function isNormalSubrecipeRow(ing: SubrecipeIngredient): boolean {
    return !ing.isCustom && !ing.nestedSubrecipeId;
}

function rowKey(row: { platform?: string; articleId?: string; productName?: string }): string {
    return productKey(row.platform ?? '', row.articleId, row.productName);
}

function alternativeKeys(alternatives: IngredientAlternative[] | undefined): string[] {
    return (alternatives ?? []).map(a => productKey(a.platform, a.articleId, a.productName));
}

/**
 * Every OTHER cost-price dish and subrecipe carrying the same primary product.
 *
 * One target per ENTITY, not per row: a dish listing the same product twice is
 * already ambiguous for waste attribution (the in-dish alternatives save has the
 * same caveat), so it appears once — with the first matching row's quantity — and
 * the apply writes to every matching row.
 *
 * CHOICE analyses are excluded on purpose: they are system-managed shared
 * per-choice cost prices, not dishes the user browses.
 *
 * Scope comes from the SOURCE analysis's own restaurant, not the selected-restaurant
 * store — in the all-restaurants view the list spans several restaurants and a
 * propagation must never leave the one it started in.
 */
export function findAlternativeTargets(
    primaryKey: string,
    sourceCostPriceId: string,
    costPriceList: CostPriceAnalysis[],
    subrecipes: Subrecipe[]
): AlternativeTarget[] {
    const source = costPriceList.find(analysis => analysis.id === sourceCostPriceId);
    if (!source) return [];
    const restaurantId = source.restaurantId;
    const targets: AlternativeTarget[] = [];

    costPriceList.forEach(analysis => {
        if (analysis.id === sourceCostPriceId) return;
        if (analysis.restaurantId !== restaurantId) return;
        if (analysis.costPriceType === COST_PRICE_TYPE_CHOICE) return;
        const match = analysis.ingredients.find(ing => isNormalCostPriceRow(ing) && rowKey(ing) === primaryKey);
        if (!match) return;
        targets.push({
            kind: 'costPrice',
            id: analysis.id,
            name: analysis.costPriceName || analysis.menuItemName || '',
            primaryQuantity: match.quantityNeeded ?? 0,
            primaryUnit: match.quantityInfo?.stdUnit ?? '',
            existingAlternativeKeys: alternativeKeys(match.alternatives),
        });
    });

    subrecipes.forEach(subrecipe => {
        if (subrecipe.restaurantId !== restaurantId) return;
        const match = subrecipe.ingredients.find(ing => isNormalSubrecipeRow(ing) && rowKey(ing) === primaryKey);
        if (!match) return;
        targets.push({
            kind: 'subrecipe',
            id: subrecipe.id,
            name: subrecipe.name,
            primaryQuantity: match.quantityNeeded ?? 0,
            primaryUnit: match.quantityInfo?.stdUnit ?? '',
            existingAlternativeKeys: alternativeKeys(match.alternatives),
        });
    });

    return targets.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The alternative's per-portion quantity for a target dish, keeping the ratio the
 * user set on the source dish: 150 g primary → 150 g alternative becomes 80 g on a
 * dish using 80 g. Ratio (not a straight copy of the target's primary quantity) so
 * it stays right when the alternative is measured in another unit — 1 piece per
 * 150 g becomes 1.33 pieces at 200 g.
 *
 * Falls back to the source quantity when either side has no usable quantity.
 */
export function scaleAlternativeQuantity(
    sourceAlternativeQuantity: number,
    sourcePrimaryQuantity: number,
    targetPrimaryQuantity: number
): number {
    if (!(sourcePrimaryQuantity > 0) || !(targetPrimaryQuantity > 0)) return sourceAlternativeQuantity;
    const scaled = sourceAlternativeQuantity * (targetPrimaryQuantity / sourcePrimaryQuantity);
    return Math.round(scaled * 100) / 100;
}

/** Merge one alternative into a row's list — never replaces what is already there. */
function mergeAlternative(
    existing: IngredientAlternative[] | undefined,
    alternative: IngredientAlternative,
    quantityNeeded: number
): IngredientAlternative[] {
    const current = existing ?? [];
    const altKey = productKey(alternative.platform, alternative.articleId, alternative.productName);
    if (current.some(a => productKey(a.platform, a.articleId, a.productName) === altKey)) return current;
    // The id is regenerated server-side on every save; a local one just keeps the
    // shape valid until then.
    return [...current, { ...alternative, id: `temp-alt-${Date.now()}-${Math.random()}`, quantityNeeded }];
}

/**
 * Write the alternative into one target, at the quantity shown in the panel.
 * Fresh-fetches first (row ids and alternative ids regenerate on every save, so the
 * store copy is never a safe write base) and updates the store on success.
 */
export async function applyAlternativeToTarget(
    target: AlternativeTarget,
    primaryKey: string,
    alternative: IngredientAlternative,
    quantityNeeded: number
): Promise<void> {
    if (target.kind === 'costPrice') {
        const current = await fetchCostPriceAnalysisById(target.id);
        const ingredients = current.ingredients.map(ing => {
            if (!isNormalCostPriceRow(ing) || rowKey(ing) !== primaryKey) return toIngredientUpdatePayload(ing);
            return {
                ...toIngredientUpdatePayload(ing),
                alternatives: mergeAlternative(ing.alternatives, alternative, quantityNeeded),
            };
        });
        const updated = await updateCostPriceAnalysis({ id: target.id, expectedUpdatedAt: recipeWriteVersion(current), ingredients });
        useCostPriceStore.getState().upsertOne(updated);
        return;
    }

    const current = await fetchSubrecipeById(target.id);
    const ingredients = current.ingredients.map(ing => {
        const { id, ...rest } = ing;
        if (!isNormalSubrecipeRow(ing) || rowKey(ing) !== primaryKey) return rest;
        return { ...rest, alternatives: mergeAlternative(ing.alternatives, alternative, quantityNeeded) };
    });
    await updateSubrecipe({ id: target.id, expectedUpdatedAt: recipeWriteVersion(current), ingredients });
}

/** Subrecipes have no per-item store upsert — one reload after a batch touches them. */
export async function reloadSubrecipes(): Promise<void> {
    await useSubrecipesStore.getState().reload();
}
